"use client"

import { createContext, useContext, useEffect, useState } from "react"
import { FullStory, init } from "@fullstory/browser"

type TFullStoryStatus = "not-initialized" | "initializing" | "ready" | "error"

interface FullStoryData {
  status: TFullStoryStatus
}

const initialState: FullStoryData = {
  status: "not-initialized",
}

const FullStoryContext = createContext<FullStoryData>(initialState)

export function FullStoryProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<TFullStoryStatus>("not-initialized")

  useEffect(() => {
    if (status === "not-initialized") {
      const fullStoryOrgID = process.env.NEXT_PUBLIC_FULLSTORY_ORG_ID

      setStatus("initializing")

      if (fullStoryOrgID) {
        init(
          {
            orgId: fullStoryOrgID,
            devMode: process.env.NODE_ENV === "development",
          },
          ({ sessionUrl }) => {
            console.debug(`Started FullStory session: ${sessionUrl}`)
            setStatus("ready")
          }
        )
      } else {
        console.error("FullStory Org ID is not set")
        setStatus("error")
      }
    }
  }, [status])

  return (
    <FullStoryContext.Provider
      value={{
        status,
      }}
    >
      {children}
    </FullStoryContext.Provider>
  )
}

export function useFullStory() {
  const { status } = useContext(FullStoryContext)

  /**
   * Tracks an event using FullStory.
   *
   * @param {Object} params - The event parameters.
   * @param {string} params.name - The name of the event.
   * @param {object} params.properties - The properties of the event.
   * @param {object} [params.schema] - The schema of the event (optional).
   * @param {string} [params.source] - The source of the event (optional).
   *
   * @returns {void}
   *
   * @throws Will log an error if FullStory is not ready.
   *
   * @link https://developer.fullstory.com/browser/capture-events/analytics-events/
   */
  function trackEvent({ name, properties, schema, source }: { name: string; properties: object; schema?: object; source?: string }) {
    if (status !== "ready") {
      console.error("FullStory is not ready")
      return
    }

    FullStory(
      "trackEvent",
      {
        name,
        properties,
        schema,
      },
      source
    )
  }

  /**
   * Sets properties for FullStory tracking.
   *
   * @param {Object} params - The parameters for setting properties.
   * @param {"document" | "page" | "user"} params.type - The type of properties being set.
   * @param {Object} params.properties - The properties to set.
   * @param {Object} [params.schema] - Optional schema for the properties.
   * @param {string} [params.source] - Optional source of the properties.
   * @returns {void}
   *
   * @throws Will log an error if FullStory is not ready.
   *
   * @link https://developer.fullstory.com/browser/identification/set-user-properties/
   * @link https://developer.fullstory.com/browser/set-page-properties/
   */
  function setProperties({ properties, type, schema, source }: { type: "document" | "page" | "user"; properties: object; schema?: object; source?: string }) {
    if (status !== "ready") {
      console.error("FullStory is not ready")
      return
    }

    FullStory(
      "setProperties",
      {
        type,
        properties,
        schema,
      },
      source
    )
  }

  /**
   * Sets the identity of a user in FullStory.
   *
   * @param {Object} params - The parameters for setting the identity.
   * @param {string} [params.uid] - The unique identifier for the user.
   * @param {boolean} [params.anonymous] - Whether the user is anonymous. If true, the uid is not required (nor used) and any other parameters are ignored. It is used to anonymize the user (for example, when a user logs out).
   * @param {object} [params.properties] - Optional properties associated with the user.
   * @param {object} [params.schema] - Optional schema for the properties.
   * @param {string} [params.source] - Optional source of the identity information.
   *
   * @returns {void}
   *
   * @throws {Error} If FullStory is not ready.
   *
   * @link https://developer.fullstory.com/browser/identification/identify-users/
   * @link https://developer.fullstory.com/browser/identification/anonymize-users/
   */
  function setIdentity({ uid, anonymous, properties, schema, source }: { uid?: string; anonymous?: boolean; properties?: object; schema?: object; source?: string }) {
    if (status !== "ready") {
      console.error("FullStory is not ready")
      return
    }

    if (!anonymous && !uid) {
      console.error("Missing uid for setIdentity and anonymous is not set")
      return
    }

    FullStory(
      "setIdentity",
      {
        ...(anonymous
          ? { anonymous }
          : {
              uid,
              properties,
              schema,
            }),
      },
      source
    )
  }

  return {
    isReady: status === "ready",
    status,
    trackEvent,
    setProperties,
    setIdentity,
  }
}
